import {inverseRealFourierTransform} from "./FFT.js";
import {spectralBank,spectralTimes,spectralNoiseEdges} from "./AcousticSpectra.js";

// Magnitude resynthesis, not sample playback or a physical waveguide. Each
// partial has its own attack/sustain trajectory, register and dynamic response.
// The engine's phase remains authoritative: no recorded vibrato or detuning.
export const spectralModes = ["spectral-violin","spectral-viola","spectral-cello","spectral-flute","spectral-clarinet","spectral-sax"];
const families = ["violin","viola","cello","flute","clarinet","sax"];
const length=1024;
type Bank={waves:Float32Array[][];noise:number[][][]};
const cache=new Map<string,Bank>();

function bankFor(family:string,midi:number,rate:number,ceiling:number,harmonics:readonly number[]):Bank {
    const key=[family,midi,rate,ceiling,harmonics.join(",")].join(":");
    const found=cache.get(key);if(found)return found;
    const layers=spectralBank[family];
    const pitches=Array.from(new Set(layers.map(x=>x.midi))).sort((a,b)=>a-b);
    const lower=pitches.filter(p=>p<=midi).pop()??pitches[0];
    const upper=pitches.find(p=>p>=midi)??pitches[pitches.length-1];
    const blend=upper===lower?0:(midi-lower)/(upper-lower);
    const waves:Float32Array[][]=[],noise:number[][][]=[];
    const highest=440*Math.pow(2,(ceiling-69)/12);
    for(let dynamic=0;dynamic<2;dynamic++) {
        const a=layers.find(x=>x.midi===lower&&x.dynamic===dynamic)!;
        const b=layers.find(x=>x.midi===upper&&x.dynamic===dynamic)!;
        waves[dynamic]=[];noise[dynamic]=[];
        for(let frame=0;frame<spectralTimes.length;frame++) {
            const data=new Float32Array(length),wave=new Float32Array(length+1);
            let energy=0;
            for(let h=1;h<=64;h++) {
                // Smoothly approach zero well before Nyquist. Ceiling uses the
                // higher tick-end pitch, with a semitone of additional margin.
                const taper=Math.max(0,Math.min(1,(rate*.46-h*highest)/(rate*.08)));
                const control=harmonics[Math.min(h-1,27)]??7;
                const balance=control===0?0:Math.pow(2,control-7)*Math.sqrt(control/7);
                const amplitude=(a.frames[frame][h-1]*(1-blend)+b.frames[frame][h-1]*blend)*taper*balance;
                const sign=Math.sin(h*h*12.9898)>0?1:-1;
                data[length-h]=amplitude*sign;energy+=amplitude*amplitude;
            }
            inverseRealFourierTransform(data,length);
            let rms=0;for(const x of data)rms+=x*x;rms=Math.sqrt(rms/length);
            const scale=rms>0?1.8*Math.sqrt(energy)/rms:0;
            for(let i=0;i<length;i++)wave[i]=data[i]*scale;
            wave[length]=wave[0];waves[dynamic].push(wave);
            noise[dynamic].push(a.noiseBands[frame].map((value,i)=>(value*(1-blend)+b.noiseBands[frame][i]*blend)*Math.sqrt(energy)));
        }
    }
    const result={waves,noise};cache.set(key,result);
    // Bounded shared cache; active voices retain their bank even after eviction.
    if(cache.size>48)cache.delete(cache.keys().next().value!);
    return result;
}

export class SpectralVoice {
    private age=0;
    private rate=44100;
    private midi:number|null=null;
    private bank:Bank|null=null;
    private previous:Bank|null=null;
    private transition=1;
    private transitionStep=1;
    private texture=0;
    private motion=1;
    private seed=1;
    private noiseRate=0;
    private noiseFilters=spectralNoiseEdges.slice(1).map(()=>({low:0,high:0,lowRate:0,highRate:0,gain:0}));
    private bandSeed=1;
    private bandCountdown=0;
    private bandInterval=1;
    private bandRate=0;
    private bands=[0,1,2].map(()=>({b0:0,a1:0,a2:0,x1:0,x2:0,y1:0,y2:0,motion:0,target:0}));
    public reset(seed=1):void {
        this.age=0;this.midi=null;this.bank=this.previous=null;
        this.seed=seed||1;this.transition=1;
        for(const f of this.noiseFilters)f.low=f.high=0;
        this.bandSeed=(seed^0x5bd1e995)||1;this.bandCountdown=0;
        for(const b of this.bands)b.x1=b.x2=b.y1=b.y2=b.motion=b.target=0;
    }
    public configure(mode:number,rate:number,frequency:number,texture:number,motion:number,harmonics:readonly number[]=[]):void {
        this.rate=rate;
        const pitch=69+12*Math.log2(frequency/440);
        if(this.midi===null)this.midi=Math.round(pitch);
        const next=bankFor(families[mode],this.midi,rate,Math.ceil(pitch)+1,harmonics);
        if(this.bank!==next){this.previous=this.bank;this.bank=next;this.transition=this.previous?0:1;}
        this.transitionStep=1/(rate*.005);
        this.texture=texture/100;this.motion=motion/100;
        if(this.noiseRate!==rate){
            this.noiseRate=rate;
            for(let i=0;i<this.noiseFilters.length;i++){
                const f=this.noiseFilters[i];
                const a=f.lowRate=1-Math.exp(-2*Math.PI*Math.min(spectralNoiseEdges[i+1],rate*.44)/rate);
                const b=f.highRate=1-Math.exp(-2*Math.PI*Math.min(spectralNoiseEdges[i],rate*.4)/rate);
                // Exact steady-state variance of two first-order lowpasses
                // driven by the same unit-variance white noise. Each band gets
                // an independent stream, so measured powers add, not amplitudes.
                const variance=(a/(2-a)+b/(2-b)-2*a*b/(a+b-a*b))/3;
                f.gain=variance>1e-10?1/Math.sqrt(variance):0;
            }
        }
        // Small independent gain movement around fixed body/radiation bands
        // complements the measured slow envelopes. No oscillator detuning.
        const frequencies=[[450,1500,3200],[300,1100,2500],[150,650,2100],[700,2000,5000],[400,1500,3500],[600,1700,3800]][mode];
        this.bandInterval=Math.max(1,Math.round(rate*.083));this.bandRate=1-Math.exp(-2*Math.PI*5/rate);
        for(let i=0;i<3;i++){
            const w=2*Math.PI*Math.min(frequencies[i],rate*.35)/rate,alpha=Math.sin(w)/(2*1.1),a0=1+alpha,b=this.bands[i];
            b.b0=alpha/a0;b.a1=-2*Math.cos(w)/a0;b.a2=(1-alpha)/a0;
        }
    }
    private lookup(wave:Float32Array,phase:number):number {
        const x=(phase-Math.floor(phase))*length,i=x|0;
        return wave[i]+(wave[i+1]-wave[i])*(x-i);
    }
    private sample(bank:Bank,frame:number,ratio:number,dynamic:number,a:number,b:number,sign:number):number {
        let out=0;
        for(let d=0;d<2;d++){
            const w=bank.waves[d][frame],v=bank.waves[d][frame+1];
            const first=this.lookup(w,a)+this.lookup(w,b)*sign;
            const second=this.lookup(v,a)+this.lookup(v,b)*sign;
            out+=(first+(second-first)*ratio)*(d?dynamic:1-dynamic);
        }
        return out;
    }
    public process(phaseA:number,phaseB:number,unisonSign:number,size:number):number {
        if(!this.bank)return 0;
        let time=this.age/this.rate;
        if(time>1.6){
            // Continuous ping-pong through late spectra, not the attack. Motion
            // adjusts this late evolution; pitch still comes only from phases.
            const p=(time-1.6)%2;
            time=1.6-(p<=1?p:2-p)*this.motion;
        }
        let frame=0;while(frame<spectralTimes.length-2&&time>spectralTimes[frame+1])frame++;
        const ratio=Math.max(0,Math.min(1,(time-spectralTimes[frame])/(spectralTimes[frame+1]-spectralTimes[frame])));
        const dynamic=Math.max(0,Math.min(1,(size-1)/2));
        let output=this.sample(this.bank,frame,ratio,dynamic,phaseA,phaseB,unisonSign);
        if(this.previous&&this.transition<1){
            const old=this.sample(this.previous,frame,ratio,dynamic,phaseA,phaseB,unisonSign);
            output=old+(output-old)*this.transition;
            this.transition=Math.min(1,this.transition+this.transitionStep);
        }
        if(--this.bandCountdown<=0){
            this.bandCountdown=this.bandInterval;
            for(const b of this.bands){let r=this.bandSeed;r^=r<<13;r^=r>>>17;r^=r<<5;this.bandSeed=r;b.target=(r>>>0)/2147483648-1;}
        }
        const source=output;
        for(const b of this.bands){
            b.motion+=this.bandRate*(b.target-b.motion);
            const y=b.b0*(source-b.x2)-b.a1*b.y1-b.a2*b.y2;
            b.x2=b.x1;b.x1=source;b.y2=b.y1;b.y1=y;
            output+=y*.25*this.motion*b.motion;
        }
        let residual=0,residualPower=0;
        for(let i=0;i<this.noiseFilters.length;i++){
            let amplitude=0;
            for(let d=0;d<2;d++){
                const now=this.bank.noise[d][frame][i]*(1-ratio)+this.bank.noise[d][frame+1][i]*ratio;
                const old=this.previous&&this.transition<1?this.previous.noise[d][frame][i]*(1-ratio)+this.previous.noise[d][frame+1][i]*ratio:now;
                amplitude+=(old+(now-old)*this.transition)*(d?dynamic:1-dynamic);
            }
            let x=this.seed;x^=x<<13;x^=x>>>17;x^=x<<5;this.seed=x;
            const random=(x>>>0)/2147483648-1,f=this.noiseFilters[i];
            f.low+=f.lowRate*(random-f.low);f.high+=f.highRate*(random-f.high);
            residual+=(f.low-f.high)*f.gain*amplitude;residualPower+=amplitude*amplitude;
        }
        // Ratios are relative to the tonal source, not raw RNG amplitude.
        // Limit total residual to guard against room/ensemble leakage; preserve
        // the measured band balance and note-age trajectory within that limit.
        const residualLimit=Math.min(1,.35/Math.max(1e-9,Math.sqrt(residualPower)));
        output+=residual*3.6*residualLimit*this.texture*1.5;
        // Avoid a hard start even when the source recording was tightly cut.
        output*=Math.min(1,this.age/(this.rate*.008));
        this.age++;
        return output;
    }
}
